/* eslint-disable no-throw-literal */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { orderBy } from 'lodash';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy
} from '@dnd-kit/sortable';

import {
  CreatePipelineModal,
  LeadModal,
  PipelineStage,
  PipelineStageForm,
  SalesPageActions,
  PipelineLabelsModal,
  SpecialitiesModal,
  RankingAndActivityModal,
  SalesFiltersModal
} from '../../../components/sales';
import ConfirmationModal from '../../../components/shared/modal/ConfirmationModal';
import LoadScreen from '../../../components/load-screen';
import Button from '../../../components/shared/Button';
import LeadCard from '../../../components/sales/LeadCard';

import {
  getPipelines,
  createPipeline,
  deletePipeline,
  updatePipeline,
  getPipelineStages,
  updatePipelineStage,
  createPipelineStage,
  deletePipelineStage,
  getPipelineLabels,
  createPipelineLabel,
  updatePipelineLabel,
  deletePipelineLabel,
  setSelectedPipeline,
} from '../../../actions/salesActions';
import {
  createLead,
  updateLead,
  getLeadsWithActivity,
  setLeads,
  bulkUpdateLeads,
  exportLeads,
  sendNewSurgeonCreatedEmail,
} from '../../../actions/leadsActions';
import {
  createSurgeon,
  getSurgeons,
  createSpeciality,
  getSpecialities,
  updateSpeciality,
  deleteSpeciality,
} from '../../../actions/surgeonsActions';
import { getProcedures } from '../../../actions/proceduresActions';
import { updateTenant } from '../../../actions/tenantActions';

import { useAlert } from '../../../hooks';

import routes from '../../../constants/routes';
import { ACTIVE_SURGEONS_PIPELINE_ID, LOST_LEADS_PIPELINE_ID } from '../../../constants/sales';

import './sales-page.scss';

const SalesLeadsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { showAlert } = useAlert();

  const currentUser = useSelector((state) => state.user.currentUser);
  const currentTenant = useSelector((state) => state.tenant.currentTenant);
  const pipelines = useSelector((state) => state.pipelines?.list?.filter((p) => p.id !== LOST_LEADS_PIPELINE_ID && p.id !== ACTIVE_SURGEONS_PIPELINE_ID) || []);
  const leads = useSelector((state) => state.leads?.list?.filter((l) => !!l.active) || []);
  const labels = useSelector((state) => state.pipelines?.labels || []);
  const selectedPipeline = useSelector((state) => state.pipelines?.selectedPipeline);
  const surgeons = useSelector((state) => state.surgeons.list);
  const specialities = useSelector((state) => state.surgeons?.specialities || []);
  const procedures = useSelector((state) => state.procedures.list);
  const users = useSelector((state) => state.users.list);

  const [fetching, setFetching] = useState(false);

  const [editMode, toggleEditMode] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeLeadId, setActiveLeadId] = useState(null);

  const [createPipelineModalOpen, toggleCreatePipelineModal] = useState(false);
  const [editPipelineModalOpen, toggleEditPipelineModal] = useState(false);
  const [deletePipelineModalOpen, toggleDeletePipelineModal] = useState(false);
  const [deleteStageModalOpen, toggleDeleteStageModal] = useState(false);
  const [editLabelsModalOpen, toggleEditLabelsModal] = useState(false);
  const [editSpecialitiesModalOpen, toggleEditSpecialitiesModal] = useState(false);
  const [leadModalOpen, toggleLeadModal] = useState(false);
  const [rankingModalOpen, toggleRankingModal] = useState(false);
  const [filtersModalOpen, toggleFiltersModal] = useState(false);

  const [createPipelineLoading, setCreatePipelineLoading] = useState(false);
  const [deletePipelineLoading, setDeletePipelineLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteStageLoading, setDeleteStageLoading] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [exportLoading, toggleExportLoading] = useState(false);

  const [pipelineStages, setPipelineStages] = useState([]);
  const [allStages, setAllStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [filters, setFilters] = useState({
    owners: [],
    surgeons: [],
    labels: [],
    rottingOnly: false,
  });

  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const scrollContainerRef = useRef();

  const updateShadows = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setShowLeftShadow(container.scrollLeft > 0);
    setShowRightShadow(container.scrollLeft < container.scrollWidth - container.clientWidth);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', updateShadows);
    window.addEventListener('resize', updateShadows);

    return () => {
      container.removeEventListener('scroll', updateShadows);
      window.removeEventListener('resize', updateShadows);
    };
  }, []);

  useEffect(() => {
    onLoad().catch();
    // const unsubscribe = dispatch(subscribeToPipelines());
    //
    // return () => {
    //   unsubscribe();
    // };
  }, []);

  const onLoad = async () => {
    try {
      setFetching(true);
      const res = await dispatch(getPipelines());
      const activePipelines = res ? res.filter((pipeline) => pipeline.id !== LOST_LEADS_PIPELINE_ID && pipeline.id !== ACTIVE_SURGEONS_PIPELINE_ID) : [];

      if (activePipelines.length) {
        if (!selectedPipeline) {
          dispatch(setSelectedPipeline(activePipelines[0].id));
        }
        const stages = await fetchAllStages(activePipelines?.map((p) => p.id)); // Fetch all stages for lead modal
        setPipelineStages(stages.filter((s) => s.pipelineId === (selectedPipeline || activePipelines[0].id)));
      }
      await Promise.all([
        dispatch(getPipelineLabels()),
        dispatch(getSurgeons()),
        dispatch(getSpecialities()),
        dispatch(getProcedures()),
        dispatch(getLeadsWithActivity(selectedPipeline || activePipelines[0].id))
      ]);
      updateShadows();
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const onSelectPipeline = async (pipelineId) => {
    try {
      dispatch(setSelectedPipeline(pipelineId));
      setFetching(true);
      await fetchPipelineStages(pipelineId);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const fetchAllStages = async (pipelineIds) => {
    try {
      let arr = [];
      for (const pipelineId of pipelineIds) {
        const res = await dispatch(getPipelineStages(pipelineId));
        if (res) {
          arr = [...arr, ...res];
        }
      }
      setAllStages(arr);
      return arr;
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPipelineStages = async (pipelineId) => {
    try {
      const res = await dispatch(getPipelineStages(pipelineId));
      if (res) {
        setPipelineStages(res || []);
        setAllStages([...allStages.filter((s) => s.pipelineId !== pipelineId), ...res]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onCreateNewPipeline = async (title) => {
    try {
      setCreatePipelineLoading(true);
      const newPipeline = await dispatch(createPipeline({ title }));
      await dispatch(getPipelines());
      setPipelineStages([]);
      toggleCreatePipelineModal(false);
      dispatch(setSelectedPipeline(newPipeline.id));
      toggleEditMode(true);
    } catch (err) {
      console.error(err);
    } finally {
      setCreatePipelineLoading(false);
    }
  };

  const onEditPipeline = async (pipelineId, title) => {
    try {
      setCreatePipelineLoading(true);
      await dispatch(updatePipeline(pipelineId, { title }));
      await dispatch(getPipelines());
      toggleEditPipelineModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setCreatePipelineLoading(false);
    }
  };

  const onDeletePipeline = async () => {
    try {
      setDeletePipelineLoading(true);

      if (leads.filter((l) => !!l.active && l.pipeline === selectedPipeline).length) {
        showAlert('warning', 'Only empty pipelines can be deleted. Please move any leads to an alternative pipeline and then try again.');
        return;
      }

      await dispatch(deletePipeline(selectedPipeline, pipelineStages.map((s) => s.id)));
      const res = await dispatch(getPipelines());
      toggleDeletePipelineModal(false);
      dispatch(setSelectedPipeline(res && res.length ? res[0].id : undefined));
      if (res && res.length) {
        await fetchPipelineStages(res[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDeletePipelineLoading(false);
    }
  };

  const onAddStageClick = () => {
    const arr = [...pipelineStages];
    arr.push({
      tempId: uuid().substring(0, 20),
      title: '',
      description: '',
      probability: 100,
      rottingPeriod: 100,
      orderNumber: arr.length + 1,
    });
    setPipelineStages(arr);
  };

  const onSaveClick = async () => {
    try {
      setSaveLoading(true);

      for (const stage of pipelineStages) {
        const data = {
          title: stage.title || '',
          description: stage.description || '',
          probability: Number(stage.probability) || 0,
          rottingPeriod: Number(stage.rottingPeriod) || 0,
          orderNumber: stage.orderNumber,
        };
        if (stage.id) {
          await dispatch(updatePipelineStage(selectedPipeline, stage.id, data));
        } else {
          await dispatch(createPipelineStage(selectedPipeline, data));
        }
      }

      await fetchPipelineStages(selectedPipeline);
      toggleEditMode(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaveLoading(false);
    }
  };

  const onDeleteStage = async () => {
    try {
      if (selectedStage) {
        setDeleteStageLoading(true);

        if (selectedStage.id) {
          await dispatch(deletePipelineStage(selectedPipeline, selectedStage.id));
          setPipelineStages([...pipelineStages].filter((s) => s.id !== selectedStage.id));
          await fetchPipelineStages(selectedPipeline);
        } else {
          setPipelineStages([...pipelineStages].filter((s) => s.tempId !== selectedStage.tempId));
        }

        toggleDeleteStageModal(false);
        setSelectedStage(null)
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteStageLoading(false);
    }
  };

  const onCreateLabel = async (name, color) => {
    try {
      await dispatch(createPipelineLabel({ name, color }));
      await dispatch(getPipelineLabels());
    } catch (err) {
      console.error(err);
    }
  };

  const onUpdateLabel = async (labelId, name, color) => {
    try {
      await dispatch(updatePipelineLabel(labelId, { name, color }));
      await dispatch(getPipelineLabels());
    } catch (err) {
      console.error(err);
    }
  };

  const onDeleteLabel = async (labelId) => {
    try {
      await dispatch(deletePipelineLabel(labelId));
      await dispatch(getPipelineLabels());
    } catch (err) {
      console.error(err);
    }
  };

  const onCreateSpeciality = async (name) => {
    try {
      await dispatch(createSpeciality({ name }));
      await dispatch(getSpecialities());
    } catch (err) {
      console.error(err);
    }
  };

  const onUpdateSpeciality = async (specialityId, name) => {
    try {
      await dispatch(updateSpeciality(specialityId, { name }));
      await dispatch(getSpecialities());
    } catch (err) {
      console.error(err);
    }
  };

  const onDeleteSpeciality = async (specialityId) => {
    try {
      await dispatch(deleteSpeciality(specialityId));
      await dispatch(getSpecialities());
    } catch (err) {
      console.error(err);
    }
  };

  const onStageChange = (stageId, field, value) => {
    const arr = [...pipelineStages];
    const stage = arr.find((s) => s.id === stageId || s.tempId === stageId);

    stage[field] = value;
    setPipelineStages(arr);
  };

  const onSubmitLead = async (formObj, newSurgeon) => {
    try {
      setLeadLoading(true);

      if (selectedLead) {
        const data = { ...formObj };
        if (formObj.stage !== selectedLead.stage) {
          const stageLeads = leads?.filter((l) => l.stage === formObj.stage);
          data.orderNumber = stageLeads.length + 1;
        }
        await dispatch(updateLead(selectedLead.id, data));
      } else {
        const data = { ...formObj };
        const stageLeads = leads?.filter((l) => l.stage === formObj.stage);
        if (newSurgeon) {
          const surgeonData = {
            ...newSurgeon,
            procedures: [],
            hospitals: [],
            qualifications: []
          }
          const surgeonRes = await dispatch(createSurgeon(surgeonData));
          data.surgeon = surgeonRes.id;

          const surgeonName = `${newSurgeon.title} ${newSurgeon.firstName} ${newSurgeon.lastName}`;
          dispatch(sendNewSurgeonCreatedEmail(surgeonName));
        }
        await dispatch(createLead({ ...data, orderNumber: stageLeads.length + 1 }));
      }

      await dispatch(getLeadsWithActivity(selectedPipeline));

      if (newSurgeon) {
        await dispatch(getSurgeons());
      }

      toggleLeadModal(false);
      setSelectedLead(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLeadLoading(false);
    }
  };

  const onRankingSubmit = async (formObj) => {
    try {
      setRankingLoading(true);
      await dispatch(updateTenant({ surgeonRanking: formObj }));
      toggleRankingModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setRankingLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = pipelineStages.findIndex(
        (s) => (s.id || s.tempId) === active.id
      );
      const newIndex = pipelineStages.findIndex(
        (s) => (s.id || s.tempId) === over?.id
      );

      const newStages = arrayMove(pipelineStages, oldIndex, newIndex).map(
        (stage, index) => ({
          ...stage,
          orderNumber: index + 1,
        })
      );
      setPipelineStages(newStages);
    }
  };

  const handleLeadDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const draggedLead = leads.find((l) => l.id === active.id);
    const overLead = leads.find((l) => l.id === over.id);
    const destinationStageId = overLead?.stage || over?.id;
    const sourceStageId = draggedLead?.stage;

    if (!draggedLead || !destinationStageId) {
      return;
    }

    // Same-stage reordering
    if (sourceStageId === destinationStageId) {
      const stageLeads = orderBy(leads.filter((l) => l.stage === sourceStageId), 'orderNumber');
      const oldIndex = stageLeads.findIndex((l) => l.id === active.id);
      const newIndex = stageLeads.findIndex((l) => l.id === over?.id);

      const newLeads = arrayMove(stageLeads, oldIndex, newIndex).map((lead, index) => ({
        ...lead,
        orderNumber: index + 1,
      }));

      const arr = [...leads];
      newLeads.forEach((lead) => {
        const updatedLead = arr.find((l) => l.id === lead.id);
        if (updatedLead) {
          updatedLead.orderNumber = lead.orderNumber;
        }
      });

      dispatch(setLeads(arr));
      dispatch(bulkUpdateLeads(newLeads));
    }

    // Cross-stage move
    if (sourceStageId !== destinationStageId) {
      const sourceLeads = orderBy(
        leads.filter((l) => l.stage === sourceStageId && l.id !== active.id),
        'orderNumber'
      );

      const destinationLeads = orderBy(
        leads.filter((l) => l.stage === destinationStageId),
        'orderNumber'
      );

      const insertIndex = overLead
        ? destinationLeads.findIndex((l) => l.id === overLead.id)
        : destinationLeads.length;

      const newDraggedLead = {
        ...draggedLead,
        stage: destinationStageId,
      };

      destinationLeads.splice(insertIndex, 0, newDraggedLead);

      const updatedSourceLeads = sourceLeads.map((lead, index) => ({
        ...lead,
        orderNumber: index + 1,
      }));

      const updatedDestinationLeads = destinationLeads.map((lead, index) => ({
        ...lead,
        orderNumber: index + 1,
      }));

      const updatedLeads = [...updatedSourceLeads, ...updatedDestinationLeads];

      const arr = [...leads];
      updatedLeads.forEach((lead) => {
        const updatedLead = arr.find((l) => l.id === lead.id);
        if (updatedLead) {
          updatedLead.stage = lead.stage;
          updatedLead.orderNumber = lead.orderNumber;
        }
      });

      dispatch(setLeads(arr));
      dispatch(bulkUpdateLeads(updatedLeads));
    }
  };

  const onLeadClick = (lead) => {
    history.push(`${routes.LEADS}/${lead.id}`);

    // setSelectedLead(lead);
    // toggleLeadModal(true);
  }

  const onExportClick = async () => {
    toggleExportLoading(true);

    try {
      await dispatch(exportLeads());
    } catch (err) {
      console.error(err);
    } finally {
      toggleExportLoading(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    })
  );

  const pipelineLeads = useMemo(() => leads.filter((l) => l.pipeline === selectedPipeline), [leads, selectedPipeline]);

  const filteredLeads = useMemo(() => {
    return pipelineLeads.filter((lead) => {
      const ownerFilter = !filters.owners?.length || filters.owners.includes(lead.owner);
      const surgeonFilter = !filters.surgeons?.length || filters.surgeons.includes(lead.surgeon);
      const labelFilter = !filters.labels?.length || lead.labels?.some((labelId) => filters.labels.includes(labelId));
      const stage = pipelineStages.find((stage) => stage.id === lead.stage);
      const rottingDays = lead?.stageUpdatedAt ? moment().startOf('day').diff(moment(lead.stageUpdatedAt), 'days') : 0;
      const rottingFilter = !filters.rottingOnly || (stage?.rottingPeriod && rottingDays > stage.rottingPeriod);

      return ownerFilter && surgeonFilter && labelFilter && rottingFilter;
    });
  }, [pipelineLeads, filters, pipelineStages]);

  const activeFilterCount = useMemo(() => (
    (filters.owners?.length || 0) + (filters.surgeons?.length || 0) + (filters.labels?.length || 0) + (filters.rottingOnly ? 1 : 0)
  ), [filters]);

  const clearFilters = () => {
    setFilters({
      owners: [],
      surgeons: [],
      labels: [],
      rottingOnly: false,
    });
  };

  return (
    <div className='sales-page__container'>
      {fetching && <LoadScreen />}

      <SalesPageActions
        userRole={currentUser.role}
        pipelines={pipelines}
        stages={pipelineStages}
        leads={filteredLeads}
        selectedPipeline={selectedPipeline}
        onSelectPipeline={onSelectPipeline}
        loading={saveLoading}
        editMode={editMode}
        onCreateClick={() => toggleCreatePipelineModal(true)}
        onEditClick={() => toggleEditMode(true)}
        onRenameClick={() => toggleEditPipelineModal(true)}
        onDeleteClick={() => toggleDeletePipelineModal(true)}
        onEditLabelsClick={() => toggleEditLabelsModal(true)}
        onEditSpecialitiesClick={() => toggleEditSpecialitiesModal(true)}
        onAddLeadClick={() => toggleLeadModal(true)}
        onCancelClick={() => toggleEditMode(false)}
        onEditRankingClick={() => toggleRankingModal(true)}
        onSaveChangesClick={onSaveClick}
        onExportClick={onExportClick}
        exportLoading={exportLoading}
        onFilterClick={() => toggleFiltersModal(true)}
        activeFilterCount={activeFilterCount}
      />

      <div
        className={`scroll-shadow-wrapper ${showLeftShadow ? 'shadow-left' : ''} ${showRightShadow ? 'shadow-right' : ''}`}
      >
        <div className='sales-page__stage-items-list' ref={scrollContainerRef}>
          {editMode ? (
            <DndContext
              collisionDetection={closestCenter}
              sensors={sensors}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={pipelineStages.map((s) => s.id || s.tempId)}
                strategy={horizontalListSortingStrategy}
              >
                {pipelineStages.map((stage) => (
                  <PipelineStageForm
                    key={stage.id || stage.tempId}
                    id={stage.id || stage.tempId}
                    stage={stage}
                    onChange={(field, value) =>
                      onStageChange(stage.id || stage.tempId, field, value)
                    }
                    onDeleteClick={() => {
                      if (leads?.filter((l) => l.stage === stage.id)) {
                        showAlert('warning', 'Only empty stages can be deleted. Please move any leads to an alternative stage and then try again.');
                        return;
                      }
                      setSelectedStage(stage);
                      toggleDeleteStageModal(true);
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleLeadDragEnd}
              onDragStart={({ active }) => setActiveLeadId(active.id)}
              onDragCancel={() => setActiveLeadId(null)}
            >
              {pipelineStages.map((stage, index) => (
                <SortableContext
                  key={stage.id}
                  items={filteredLeads.filter((l) => l.stage === stage.id).map((l) => l.id)}
                  id={stage.id}
                  strategy={rectSortingStrategy}
                >
                  <PipelineStage
                    isLast={index === pipelineStages.length - 1}
                    stage={stage}
                    leads={orderBy(filteredLeads?.filter((l) => l.stage === stage.id), 'orderNumber')}
                    surgeons={surgeons}
                    labels={labels}
                    onLeadClick={onLeadClick}
                    activeLeadId={activeLeadId}
                    tenantColor={currentTenant.colorPrimary}
                    surgeonRanking={currentTenant.surgeonRanking}
                  />
                </SortableContext>
              ))}
              <DragOverlay>
                {activeLeadId ? (
                  <div className="lead-card--overlay">
                    <LeadCard
                      isOverlay
                      lead={leads.find((l) => l.id === activeLeadId)}
                      surgeons={surgeons}
                      labels={labels}
                      tenantColor={currentTenant.colorPrimary}
                      surgeonRanking={currentTenant.surgeonRanking}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}

          {editMode && (
            <div className='sales-page__new-stage'>
              <Button
                text='Add New Stage'
                onClick={onAddStageClick}
                type='secondary'
              />
            </div>
          )}
        </div>
        <div className="scroll-shadow-left" />
        <div className="scroll-shadow-right" />
      </div>

      <CreatePipelineModal
        open={createPipelineModalOpen}
        onClose={() => toggleCreatePipelineModal(false)}
        onSubmit={onCreateNewPipeline}
        loading={createPipelineLoading}
      />

      <CreatePipelineModal
        mode='update'
        pipeline={pipelines?.find((p) => p.id === selectedPipeline)}
        open={editPipelineModalOpen}
        onClose={() => toggleEditPipelineModal(false)}
        onSubmit={onEditPipeline}
        loading={createPipelineLoading}
      />

      <PipelineLabelsModal
        open={editLabelsModalOpen}
        onClose={() => toggleEditLabelsModal(false)}
        labels={labels}
        onCreate={onCreateLabel}
        onUpdate={onUpdateLabel}
        onDelete={onDeleteLabel}
      />

      <SpecialitiesModal
        open={editSpecialitiesModalOpen}
        onClose={() => toggleEditSpecialitiesModal(false)}
        specialities={specialities}
        onCreate={onCreateSpeciality}
        onUpdate={onUpdateSpeciality}
        onDelete={onDeleteSpeciality}
      />

      <LeadModal
        open={leadModalOpen}
        onClose={() => {
          toggleLeadModal(false);
          setSelectedLead(null);
        }}
        surgeonsList={surgeons}
        proceduresList={procedures}
        labelsList={labels}
        usersList={users}
        pipelines={pipelines}
        stages={allStages}
        defaultPipeline={selectedPipeline}
        mode={!!selectedLead ? 'update' : 'create'}
        lead={selectedLead}
        loading={leadLoading}
        onSubmit={onSubmitLead}
        currentUser={currentUser}
        tenantColor={currentTenant.colorPrimary}
        surgeonRanking={currentTenant?.surgeonRanking}
      />

      <RankingAndActivityModal
        open={rankingModalOpen}
        onClose={() => toggleRankingModal(false)}
        onSubmit={onRankingSubmit}
        surgeonRanking={currentTenant.surgeonRanking}
        loading={rankingLoading}
      />

      <SalesFiltersModal
        open={filtersModalOpen}
        onClose={() => toggleFiltersModal(false)}
        filters={filters}
        onChange={setFilters}
        onClear={clearFilters}
        users={users}
        surgeons={surgeons}
        labels={labels}
        active={!!activeFilterCount}
      />

      <ConfirmationModal
        open={deletePipelineModalOpen}
        onClose={() => toggleDeletePipelineModal(false)}
        onSubmit={onDeletePipeline}
        title='Delete Pipeline'
        text='You are about to delete this pipeline. Any related leads will be removed forever.'
        submitText='Delete'
        loading={deletePipelineLoading}
      />

      <ConfirmationModal
        open={deleteStageModalOpen}
        onClose={() => toggleDeleteStageModal(false)}
        onSubmit={onDeleteStage}
        title='Delete Stage'
        text='You are about to delete this stage. Any related leads will be removed forever.'
        submitText='Delete'
        loading={deleteStageLoading}
      />
    </div>
  );
};

export default SalesLeadsPage;
